import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Shield, Database, Users, CheckCircle2, XCircle, AlertTriangle,
  RefreshCw, Loader2, Activity, Server, Play, Mail, Wrench, Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const SystemDiagnostics = () => {
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [smokeTestResult, setSmokeTestResult] = useState(null);
  const [runningSmoke, setRunningSmoke] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const fetchDiagnostics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/system-diagnostics`, {
        headers: getAuthHeaders()
      });
      setDiagnostics(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des diagnostics');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchDiagnostics();
    }
  }, []);

  const runSmokeTest = async () => {
    setRunningSmoke(true);
    setSmokeTestResult(null);
    try {
      const response = await axios.post(`${API}/admin/smoke-test`, {}, {
        headers: getAuthHeaders()
      });
      setSmokeTestResult(response.data);
      if (response.data.failed === 0) {
        toast.success(`Smoke test OK - ${response.data.passed}/${response.data.total_tested} comptes valides`);
      } else {
        toast.error(`${response.data.failed} compte(s) en echec !`);
      }
    } catch (error) {
      toast.error('Erreur lors du smoke test');
    } finally {
      setRunningSmoke(false);
    }
  };

  const repairAccounts = async () => {
    setRepairing(true);
    setRepairResult(null);
    try {
      const response = await axios.post(`${API}/admin/repair-accounts`, {}, {
        headers: getAuthHeaders()
      });
      setRepairResult(response.data);
      toast.success(response.data.message);
      // Refresh diagnostics after repair
      await fetchDiagnostics();
    } catch (error) {
      toast.error('Erreur lors de la reparation');
    } finally {
      setRepairing(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="diagnostics-access-denied">
        Acces reserve au Super Admin
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0E346E]" />
      </div>
    );
  }

  const StatusIcon = ({ ok }) => ok
    ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
    : <XCircle className="w-5 h-5 text-red-500" />;

  const StatusBadge = ({ status }) => {
    const colors = {
      OK: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      MISSING: 'bg-red-100 text-red-700 border-red-200',
      PASSWORD_MISMATCH: 'bg-amber-100 text-amber-700 border-amber-200',
      FAIL: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <Badge variant="outline" className={colors[status] || 'bg-gray-100 text-gray-700'}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="system-diagnostics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#0E346E]" />
            Diagnostic Systeme
          </h1>
          <p className="text-gray-500 mt-1">
            Etat complet de la plateforme et verification des comptes
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={fetchDiagnostics}
            disabled={loading}
            data-testid="refresh-diagnostics-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            onClick={runSmokeTest}
            disabled={runningSmoke}
            className="bg-[#0E346E] hover:bg-[#0A2850]"
            data-testid="run-smoke-test-btn"
          >
            {runningSmoke ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Test en cours...</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Smoke Test</>
            )}
          </Button>
          {diagnostics?.accounts?.some(a => !a.password_ok) && (
            <Button
              onClick={repairAccounts}
              disabled={repairing}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="repair-accounts-btn"
            >
              {repairing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reparation...</>
              ) : (
                <><Wrench className="w-4 h-4 mr-2" /> Reparer tout</>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Overall Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`border-2 ${diagnostics?.overall_status === 'healthy' ? 'border-emerald-200 bg-emerald-50' : diagnostics?.overall_status === 'warning' ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50'}`}>
          <CardContent className="pt-4 pb-3 text-center">
            <Activity className={`w-8 h-8 mx-auto mb-1 ${diagnostics?.overall_status === 'healthy' ? 'text-emerald-600' : 'text-red-600'}`} />
            <div className="font-bold text-lg uppercase">
              {diagnostics?.overall_status === 'healthy' ? 'Sain' : diagnostics?.overall_status === 'warning' ? 'Attention' : 'Critique'}
            </div>
            <div className="text-xs text-gray-500">Statut global</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <Database className={`w-8 h-8 mx-auto mb-1 ${diagnostics?.database?.status === 'connected' ? 'text-emerald-600' : 'text-red-600'}`} />
            <div className="font-bold text-lg">
              {diagnostics?.database?.status === 'connected' ? 'Connectee' : 'Erreur'}
            </div>
            <div className="text-xs text-gray-500">Base de donnees</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <Users className="w-8 h-8 mx-auto mb-1 text-[#0E346E]" />
            <div className="font-bold text-lg">{diagnostics?.database?.users || 0}</div>
            <div className="text-xs text-gray-500">Utilisateurs</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <Server className="w-8 h-8 mx-auto mb-1 text-[#0E346E]" />
            <div className="font-bold text-lg">{diagnostics?.database?.tenants || 0}</div>
            <div className="text-xs text-gray-500">Tenants</div>
          </CardContent>
        </Card>
      </div>

      {/* Issues */}
      {diagnostics?.issues?.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-red-700 flex items-center gap-2 text-base">
              <AlertTriangle className="w-5 h-5" /> Problemes detectes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {diagnostics.issues.map((issue, i) => (
                <li key={i} className="text-red-600 text-sm flex items-center gap-2">
                  <XCircle className="w-4 h-4 flex-shrink-0" /> {issue}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Account Verification */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="w-5 h-5 text-[#0E346E]" />
            Verification des comptes ({diagnostics?.accounts?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Statut</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Mot de passe</TableHead>
                <TableHead>Cause / Explication</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {diagnostics?.accounts?.map((acct, i) => (
                <TableRow key={i} data-testid={`account-row-${acct.email}`}>
                  <TableCell><StatusIcon ok={acct.password_ok} /></TableCell>
                  <TableCell className="font-medium">{acct.label}</TableCell>
                  <TableCell className="text-sm font-mono">{acct.email}</TableCell>
                  <TableCell>{acct.name || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {acct.role || '-'}
                    </Badge>
                  </TableCell>
                  <TableCell><StatusBadge status={acct.status} /></TableCell>
                  <TableCell className="text-xs text-gray-500 max-w-xs">
                    {acct.cause ? (
                      <span className="flex items-start gap-1">
                        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-500" />
                        {acct.cause}
                      </span>
                    ) : (
                      <span className="text-emerald-600">Aucun probleme</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Repair Result */}
      {repairResult && (
        <Card className={`border-2 ${repairResult.created + repairResult.password_reset > 0 ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Wrench className="w-5 h-5 text-amber-600" />
              Rapport de reparation : {repairResult.message}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="text-2xl font-bold text-emerald-700">{repairResult.created}</div>
                <div className="text-xs text-emerald-600">Comptes crees</div>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="text-2xl font-bold text-amber-700">{repairResult.password_reset}</div>
                <div className="text-xs text-amber-600">Mots de passe reinitialises</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-2xl font-bold text-gray-700">{repairResult.already_ok}</div>
                <div className="text-xs text-gray-600">Deja OK</div>
              </div>
            </div>
            <div className="space-y-1">
              {repairResult.repairs.map((r, i) => (
                <div key={i} className="flex items-center gap-3 py-1 text-sm">
                  {r.action === 'CREATED' ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">CREE</Badge>
                  ) : r.action === 'PASSWORD_RESET' ? (
                    <Badge className="bg-amber-100 text-amber-700 border-amber-200">REINIT</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600 border-gray-200">OK</Badge>
                  )}
                  <span className="font-mono text-xs">{r.email}</span>
                  <span className="text-gray-500 text-xs">{r.reason}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Repartition des roles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(diagnostics?.role_distribution || {}).map(([role, count]) => (
                <div key={role} className="flex items-center justify-between py-1 border-b border-gray-100">
                  <span className="font-medium capitalize">{role.replace('_', ' ')}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Tenants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {diagnostics?.tenants?.map((t, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-gray-100">
                  <div>
                    <span className="font-medium">{t.name}</span>
                    {t.is_demo && <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700">Demo</Badge>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{t.user_count} users</span>
                    <Badge variant="outline" className="text-xs">{t.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Smoke Test Results */}
      {smokeTestResult && (
        <Card className={`border-2 ${smokeTestResult.failed === 0 ? 'border-emerald-200' : 'border-red-200'}`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              {smokeTestResult.failed === 0
                ? <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                : <XCircle className="w-5 h-5 text-red-600" />}
              Resultat Smoke Test : {smokeTestResult.passed}/{smokeTestResult.total_tested} OK
              {smokeTestResult.alert_sent && (
                <Badge variant="outline" className="ml-2 bg-amber-50 text-amber-700">
                  <Mail className="w-3 h-3 mr-1" /> Alerte email envoyee
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {smokeTestResult.results.map((r, i) => (
                <div key={i} className="flex items-center gap-3 py-1">
                  <StatusIcon ok={r.status === 'OK'} />
                  <span className="font-medium w-32">{r.label}</span>
                  <span className="text-sm font-mono text-gray-500">{r.email}</span>
                  <StatusBadge status={r.status} />
                </div>
              ))}
            </div>
            {smokeTestResult.failures.length > 0 && (
              <div className="mt-3 p-3 bg-red-50 rounded border border-red-200">
                <p className="font-medium text-red-700 text-sm">Echecs :</p>
                {smokeTestResult.failures.map((f, i) => (
                  <p key={i} className="text-red-600 text-sm">{f}</p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SystemDiagnostics;
